import {
  DateGranularity,
  dateGranularityFromTrunc,
  DateTrunc,
  dateTruncFromGranularity,
  HistoricalComparisonSpec,
  makeHistoricalComparisonSpec,
  makeMarkBasedComparisonSpec,
  makeStaticComparisonSpec,
  MarkBasedComparisonSpec,
} from '../../models';
import {
  ComparisonSpecType,
  isStaticComparison,
  isNoComparison,
  isHistoricalComparisonSpec,
  isMarkBasedComparison,
} from '../../models/comparisonSpec/ComparisonSpecType';
import { ColumnReference, EffectiveDefinition, Features, Schema } from '../../models/EffectiveDefinition';
import { RowShiftCalculation, RowShiftType } from '../../transforms/calculations/RowShiftCalculation';
import { TimeShiftCalculation, TimeShiftType } from '../../transforms/calculations/TimeShiftCalculation';
import { getTimestampGranularity, MEASURE_COLUMN_NAME, TIMESTAMP_COLUMN_NAME } from '../metricValue';
import { STATUS_FEATURE_NAME } from '../status';
import {
  defaultThresholdLineStyle,
  definitionWithStaticTarget,
  getDefaultLineForThreshold,
  getStaticTargetValue,
  isDefaultLineStaticRefLine,
  shouldRenderFirstThresholdLine,
} from '../threshold';
import { ThresholdFeature, ThresholdLine } from '../threshold/models';
import { COMPARISON_TIMESTAMP_COLUMN_NAME, COMPARISON_MEASURE_COLUMN_NAME } from './defaults';
export { COMPARISON_MEASURE_COLUMN_NAME, COMPARISON_TIMESTAMP_COLUMN_NAME };

export const defaultComparisonSchema = (
  timestampColumnRef: ColumnReference,
  measureColumnRef: ColumnReference,
  granularity: DateGranularity
): Schema => {
  const comparison = defaultComparisonForGranularity(granularity);
  return {
    [COMPARISON_TIMESTAMP_COLUMN_NAME]: {
      type: 'datetime',
      name: COMPARISON_TIMESTAMP_COLUMN_NAME,
      calculation: calculationForComparison(comparison),
    },
    [COMPARISON_MEASURE_COLUMN_NAME]: {
      type: 'number',
      name: COMPARISON_MEASURE_COLUMN_NAME,
    },
  };
};

export const defaultComparisonForGranularity = (granularity: DateGranularity): ComparisonSpecType => {
  if (granularity === DateGranularity.UNKNOWN) {
    return makeMarkBasedComparisonSpec('', 1);
  } else {
    return makeHistoricalComparisonSpec(dateTruncFromGranularity(granularity), '', 1);
  }
};

export const makeMarkBasedOrHistoricalComparisonSpec = (
  numberOfUnits: number,
  dateTrunc: DateTrunc,
  shouldShowComparisonLine?: boolean
): HistoricalComparisonSpec | MarkBasedComparisonSpec => {
  return dateTrunc === DateTrunc.UNKNOWN
    ? makeMarkBasedComparisonSpec('', numberOfUnits)
    : makeHistoricalComparisonSpec(dateTrunc, '', numberOfUnits, shouldShowComparisonLine);
};

export const getComparisonFromDefinitionAndFlags = (
  flags: {
    isReferenceLineEnabled?: boolean;
    isHistoricalComparisonsEnabled?: boolean;
  },
  definition: EffectiveDefinition
): ComparisonSpecType | undefined => {
  const dateGranularity = getTimestampGranularity(definition);
  const dateTrunc = dateTruncFromGranularity(dateGranularity);

  if (flags.isReferenceLineEnabled && isDefaultLineStaticRefLine(definition)) {
    const staticTargetValue = getStaticTargetValue(definition);
    return staticTargetValue === undefined ? undefined : makeStaticComparisonSpec('', staticTargetValue);
  }

  if (flags.isHistoricalComparisonsEnabled) {
    const numberOfUnits = getNumberOfUnitsOrAmount(definition);
    const shouldShowComparisonLine = flags.isReferenceLineEnabled && shouldRenderFirstThresholdLine(definition);
    return numberOfUnits === undefined
      ? undefined
      : makeMarkBasedOrHistoricalComparisonSpec(numberOfUnits, dateTrunc, shouldShowComparisonLine);
  }

  return defaultComparisonForGranularity(dateGranularity ?? DateGranularity.UNKNOWN);
};

export const definitionWithComparison = (
  definition: EffectiveDefinition,
  comparison: ComparisonSpecType,
  hideThresholdLine: boolean
): EffectiveDefinition => {
  const comparisonName = 'comparison';

  if (isStaticComparison(comparison)) {
    return definitionWithStaticTarget(definition, comparison.value);
  }

  const newFeatures = makeFeaturesWithComparison(hideThresholdLine, comparison, definition);
  const newSchema = makeSchemaWithComparison(definition, comparison, comparisonName);

  return { ...definition, features: newFeatures, schema: newSchema };
};

export const getNumberOfUnitsOrAmount = (definition: EffectiveDefinition): number | undefined => {
  if (isDefaultLineStaticRefLine(definition)) {
    return undefined;
  }
  const line = getDefaultLineForThreshold(definition.features.threshold);
  if (line?.timestamp === undefined) {
    return undefined;
  }
  const timestampCalculation = definition.schema[line.timestamp.column].calculation;
  return timestampCalculation?.numberOfUnits ?? timestampCalculation?.amount;
};

export const getComparisonGranularity = (definition: EffectiveDefinition): DateGranularity | undefined => {
  if (isDefaultLineStaticRefLine(definition)) {
    return undefined;
  }

  const line = getDefaultLineForThreshold(definition.features.threshold);
  if (line?.timestamp === undefined) {
    return undefined;
  }

  const timestampCalculation = definition.schema[line.timestamp.column].calculation;
  if (!timestampCalculation) {
    return undefined;
  }
  return timestampCalculation.granularity;
};

const makeFeaturesWithComparison = (
  hideThresholdLine: boolean,
  comparison: ComparisonSpecType,
  definition: EffectiveDefinition
): Features => {
  const definitionWithoutStatus = { ...definition.features };
  delete definitionWithoutStatus[STATUS_FEATURE_NAME];

  if (isNoComparison(comparison)) {
    // No comparison was set so remove the existing thresholds
    return { ...definitionWithoutStatus, threshold: {} };
  }

  // For now, we're swapping a new threshold if one already exists because we can (at the moment) only
  // have one threshold applied to a metric
  const newThresholdLine: ThresholdLine = {
    measure: { column: COMPARISON_MEASURE_COLUMN_NAME },
    timestamp: { column: COMPARISON_TIMESTAMP_COLUMN_NAME },
    style: {
      ...defaultThresholdLineStyle(),
      hidden: hideThresholdLine,
    },
  };
  const newThreshold: ThresholdFeature = {
    thresholds: [
      {
        lines: [newThresholdLine],
      },
    ],
  };

  const newFeatures = { ...definitionWithoutStatus, threshold: newThreshold };
  return newFeatures;
};

const makeSchemaWithComparison = (
  definition: EffectiveDefinition,
  comparison: ComparisonSpecType,
  comparisonName: string
): Schema => {
  if (isNoComparison(comparison)) {
    // No comparison was set so don't modify the schema
    return { ...definition.schema };
  }

  return {
    ...definition.schema,
    [COMPARISON_TIMESTAMP_COLUMN_NAME]: {
      type: 'datetime',
      name: COMPARISON_TIMESTAMP_COLUMN_NAME,
      calculation: calculationForComparison(comparison),
    },
    [COMPARISON_MEASURE_COLUMN_NAME]: {
      type: 'number',
      name: COMPARISON_MEASURE_COLUMN_NAME,
    },
  };
};

const calculationForComparison = (
  comparison: ComparisonSpecType
): TimeShiftCalculation | RowShiftCalculation | undefined => {
  if (isHistoricalComparisonSpec(comparison)) {
    return {
      type: TimeShiftType,
      source: { column: TIMESTAMP_COLUMN_NAME },
      granularity: dateGranularityFromTrunc(comparison.comparisonPeriod),
      numberOfUnits: comparison.numberOfPeriods ?? 1,
      copy: [{ from: { column: MEASURE_COLUMN_NAME }, to: { column: COMPARISON_MEASURE_COLUMN_NAME } }],
    };
  } else if (isMarkBasedComparison(comparison)) {
    return {
      type: RowShiftType,
      amount: comparison.numberOfMarks,
      source: { column: TIMESTAMP_COLUMN_NAME },
      copy: [{ from: { column: MEASURE_COLUMN_NAME }, to: { column: COMPARISON_MEASURE_COLUMN_NAME } }],
    };
  } else {
    return undefined;
  }
};
